"use client";

/**
 * The first open after launch (spec 2026-10-14 §3.1; mockups/bash-phone-2026-10-14.html,
 * chapter 3): a person with no number yet is offered his, once, in his words, and the
 * first call opens in the same dialog only after a yes. Asked once per browser, here or
 * in onboarding (asked.ts); "Not now", or closing it, is fine. Silent while the phone is
 * dark: the status read is a 404, which is null. Someone already verified (a number on
 * the status, or its state) is never asked, and counts as asked from then on. Someone on
 * the free plan is not asked either (Bash's phone comes with a paid plan), and is not
 * counted as asked, so a later upgrade still brings the ask.
 */
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { getPhoneStatus } from "@/lib/api/phone";
import { BashFace } from "./BashFace";
import { FirstCallFlow, PAGE_SKIN } from "./FirstCallFlow";
import { markPhoneAsked, phoneAsked } from "./asked";
import { PHONE_COPY } from "./copy";

export function PhoneNudge() {
  const pathname = usePathname();
  const [ask, setAsk] = useState<{ suggested?: string } | null>(null);
  const [yes, setYes] = useState(false);

  useEffect(() => {
    if (phoneAsked() || pathname?.startsWith("/account/phone")) return;
    let live = true;
    getPhoneStatus()
      .then((s) => {
        if (!live || !s || s.paidPlan === false) return;
        if (s.numbers.length > 0 || s.state === "verified") {
          markPhoneAsked();
          return;
        }
        if (s.state !== "calling") setAsk({ suggested: s.suggested ?? undefined });
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
    // Once per app load: the shell mounts it once, and a route change is not a new open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const close = () => {
    markPhoneAsked();
    setAsk(null);
  };

  return (
    <Dialog open={ask !== null} onOpenChange={(open) => !open && close()}>
      <DialogContent
        className="border sm:max-w-[440px]"
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">{PHONE_COPY.title}</DialogTitle>
        <div className="flex flex-col items-center gap-3 text-center">
          {yes ? (
            <FirstCallFlow
              title={PHONE_COPY.numberTitle}
              lede={null}
              suggested={ask?.suggested}
              onDone={close}
              onSkip={close}
            />
          ) : (
            <>
              <BashFace className="size-14" alt={PHONE_COPY.bash} />
              <h2 className={PAGE_SKIN.title}>{PHONE_COPY.offerTitle}</h2>
              <p className={PAGE_SKIN.lede}>{PHONE_COPY.offerLede}</p>
              <div className="mt-2 flex gap-2.5">
                <Button size="sm" type="button" onClick={() => setYes(true)}>
                  {PHONE_COPY.yes}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  type="button"
                  onClick={close}
                >
                  {PHONE_COPY.notNow}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
